/**
 * Show Notes Preview Modal
 * Displays a near-full screen preview of show notes with markdown rendering and images
 */

import React from 'react'
import {
  Box,
  Dialog,
  IconButton,
  Typography,
  Card,
  CardContent,
  CircularProgress,
} from '@mui/material'
import { Close, Visibility } from '@mui/icons-material'
import { MuiMarkdown } from 'mui-markdown'
import { markdownStyles } from 'src/v3/utilities/markdown-styles'
import {
  ShowNotesBlock,
  ShowNotesTextBlock,
  ShowNotesImageBlock,
  ShowNotesPDFBlock,
} from '../types'
import { PDFViewer } from './pdf-viewer'
import { usePDFViewerUrl } from '../hooks/use-pdf-viewer-url'

function PDFBlockPreview({
  block,
  basePath,
}: {
  block: ShowNotesPDFBlock
  basePath: string
}) {
  let {
    data: presignedUrl,
    isLoading,
    error,
  } = usePDFViewerUrl(block.id, basePath)

  return (
    <Box sx={{ mb: 4 }}>
      <PDFViewer
        block={block}
        presignedUrl={presignedUrl}
        loading={isLoading}
        error={error}
        width={800}
      />
    </Box>
  )
}

interface ShowNotesPreviewModalProps {
  open: boolean
  onClose: () => void
  blocks: ShowNotesBlock[]
  showNotesName: string
  showNotesDescription?: string
  basePath: string
  loading?: boolean
}

export function ShowNotesPreviewModal({
  open,
  onClose,
  blocks,
  showNotesName,
  showNotesDescription,
  basePath,
  loading = false,
}: ShowNotesPreviewModalProps) {
  // Sort blocks by sequence for proper rendering order
  let sortedBlocks = blocks
    .slice()
    .toSorted((a, b) => (a.sequence || 0) - (b.sequence || 0))

  // Function to render individual blocks
  let renderBlock = (block: ShowNotesBlock) => {
    if (block.blockType === 'text') {
      let textBlock = block as ShowNotesTextBlock
      return (
        <Box key={block.id} sx={{ mb: 3 }}>
          <Box sx={markdownStyles}>
            <MuiMarkdown>{textBlock.markdown || ''}</MuiMarkdown>
          </Box>
        </Box>
      )
    }

    if (block.blockType === 'image') {
      let imageBlock = block as ShowNotesImageBlock
      if (!imageBlock.imageUrl) {
        return null // Don't render image blocks without images
      }

      return (
        <Box key={block.id} sx={{ mb: 4, textAlign: 'center' }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              mb: imageBlock.caption ? 2 : 0,
            }}
          >
            <img
              src={`${import.meta.env.VITE_CLOUDFRONT_URL}/images/${imageBlock.imageUrl.replace('originals', 'resized')}`}
              alt={
                imageBlock.altText || imageBlock.caption || 'Show notes image'
              }
              style={{
                width: `${imageBlock.widthPercent || 100}%`,
                maxWidth: '100%',
                height: 'auto',
                borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
              }}
            />
          </Box>
          {imageBlock.caption && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                fontStyle: 'italic',
                textAlign: 'center',
                maxWidth: '80%',
                mx: 'auto',
                lineHeight: 1.5,
              }}
            >
              {imageBlock.caption}
            </Typography>
          )}
        </Box>
      )
    }

    if (block.blockType === 'pdf') {
      let pdfBlock = block as ShowNotesPDFBlock
      if (!pdfBlock.pdfUrl) {
        return null // Don't render PDF blocks without PDFs
      }

      return (
        <PDFBlockPreview key={block.id} block={pdfBlock} basePath={basePath} />
      )
    }

    return null
  }

  let isEmpty = sortedBlocks.length === 0
  let hasContent = sortedBlocks.some(
    (block) =>
      (block.blockType === 'text' &&
        (block as ShowNotesTextBlock).markdown?.trim()) ||
      (block.blockType === 'image' &&
        (block as ShowNotesImageBlock).imageUrl) ||
      (block.blockType === 'pdf' && (block as ShowNotesPDFBlock).pdfUrl),
  )

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      sx={{
        '& .MuiDialog-paper': {
          width: '80vw',
          height: '80vh',
          maxWidth: 'none',
          maxHeight: 'none',
          m: 0,
        },
      }}
    >
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'background.paperDark',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 3,
            borderBottom: 1,
            borderColor: 'divider',
            flexShrink: 0,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Visibility sx={{ color: 'primary.main' }} />
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                Preview: {showNotesName}
              </Typography>
              {showNotesDescription && (
                <Typography variant="body2" color="text.secondary">
                  {showNotesDescription}
                </Typography>
              )}
            </Box>
          </Box>
          <IconButton
            onClick={onClose}
            aria-label="Close preview"
            sx={{
              bgcolor: 'rgba(255, 255, 255, 0.1)',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.2)',
              },
            }}
          >
            <Close />
          </IconButton>
        </Box>

        {/* Content Area */}
        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            p: 4,
          }}
        >
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : isEmpty || !hasContent ? (
            <Card
              variant="outlined"
              sx={{
                p: 6,
                textAlign: 'center',
                bgcolor: 'background.paper',
                borderStyle: 'dashed',
              }}
            >
              <CardContent>
                <Visibility
                  sx={{
                    fontSize: 48,
                    color: 'text.disabled',
                    mb: 2,
                  }}
                />
                <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                  No Content to Preview
                </Typography>
                <Typography variant="body2" color="text.disabled">
                  Add some text blocks, images, or PDFs to see the preview
                </Typography>
              </CardContent>
            </Card>
          ) : (
            <Box
              sx={{
                maxWidth: '900px',
                mx: 'auto',
                '& > *:first-of-type': {
                  mt: 0,
                },
                '& > *:last-child': {
                  mb: 0,
                },
              }}
            >
              {sortedBlocks.map((block) => renderBlock(block))}
            </Box>
          )}
        </Box>

        {/* Footer */}
        <Box
          sx={{
            p: 2,
            borderTop: 1,
            borderColor: 'divider',
            bgcolor: 'background.paper',
            flexShrink: 0,
            textAlign: 'center',
          }}
        >
          <Typography variant="caption" color="text.disabled">
            {sortedBlocks.length} blocks • Preview mode
          </Typography>
        </Box>
      </Box>
    </Dialog>
  )
}

export default ShowNotesPreviewModal
